/* Telegram Mini App «Производство / Склад» Ainanan — для операторов цеха.
   Бот: ainananordersalmaty_bot (web_app). Авторизация: подписанный initData. */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/production-app`;
const INITIAL_HASH = typeof window !== 'undefined' ? window.location.hash : '';
function hashInitData() {
  try {
    const sources = [INITIAL_HASH, typeof window !== 'undefined' ? window.location.hash : ''];
    for (const h of sources) {
      const v = new URLSearchParams((h || '').replace(/^#/, '')).get('tgWebAppData');
      if (v) return v;
    }
  } catch (e) { /* noop */ }
  return '';
}
const nf = new Intl.NumberFormat('ru-RU');
const money = (n) => nf.format(Math.round(n || 0)) + ' ₸';

const TABS = [
  ['receipt', '📦', 'Приход'],
  ['stock', '📊', 'Остатки'],
  ['produce', '🏭', 'Выпуск'],
  ['writeoff', '📉', 'Списание'],
  ['ops', '🕓', 'Операции'],
  ['req', '🧮', 'Закупка'],
];

const PALETTES = {
  dark: {
    page: 'bg-stone-950 text-stone-100', center: 'bg-stone-950',
    header: 'bg-stone-950/90 border-stone-800', nav: 'bg-stone-900 border-stone-800',
    navShadow: 'shadow-[0_-4px_20px_rgba(0,0,0,0.5)]',
    surface: 'bg-stone-900 border-stone-800', divide: 'divide-stone-800', border: 'border-stone-800',
    heading: 'text-stone-300', label: 'text-stone-400', muted: 'text-stone-500', strong: 'text-stone-200',
    selected: 'bg-amber-600/20', cancelBtn: 'bg-red-600/20 text-red-300', inactiveTab: 'text-stone-400',
    okMsg: 'bg-emerald-600/20 text-emerald-300', errMsg: 'bg-red-600/20 text-red-300',
    bg: '#0c0a09',
  },
  light: {
    page: 'bg-stone-50 text-stone-900', center: 'bg-stone-50',
    header: 'bg-white/90 border-stone-200', nav: 'bg-white border-stone-200',
    navShadow: 'shadow-[0_-4px_20px_rgba(0,0,0,0.08)]',
    surface: 'bg-white border-stone-200', divide: 'divide-stone-200', border: 'border-stone-200',
    heading: 'text-stone-700', label: 'text-stone-500', muted: 'text-stone-400', strong: 'text-stone-900',
    selected: 'bg-amber-100', cancelBtn: 'bg-red-100 text-red-700', inactiveTab: 'text-stone-500',
    okMsg: 'bg-emerald-100 text-emerald-700', errMsg: 'bg-red-100 text-red-700',
    bg: '#fafaf9',
  },
};

const ThemeCtx = createContext(PALETTES.dark);
const useT = () => useContext(ThemeCtx);

export default function ProductionApp() {
  const [initData, setInitData] = useState('');
  const [phase, setPhase] = useState('loading');
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('receipt');
  const [helpOpen, setHelpOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('prod_theme') === 'light' ? 'light' : 'dark'; } catch (e) { return 'dark'; }
  });
  const t = PALETTES[theme];

  const applyTgColors = useCallback((th) => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;
    try { tg.setHeaderColor?.(PALETTES[th].bg); tg.setBackgroundColor?.(PALETTES[th].bg); } catch (e) {}
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('prod_theme', next); } catch (e) {}
      applyTgColors(next);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    const tryBoot = (attempt = 0) => {
      if (cancelled) return;
      const tg = window.Telegram?.WebApp;
      const data = (tg && tg.initData) || hashInitData();
      if (tg) { try { tg.expand(); tg.ready(); } catch (e) {} applyTgColors(theme); }
      if (data) { setInitData(data); return; }
      if (attempt < 12) { setTimeout(() => tryBoot(attempt + 1), 200); return; }
      setPhase('notg');
    };
    // If Telegram data is already available (object or URL hash), boot immediately.
    if (window.Telegram?.WebApp || hashInitData()) { tryBoot(); return; }
    // Otherwise load the Telegram SDK; on error still fall back to the URL hash.
    const s = document.createElement('script');
    s.src = 'https://telegram.org/js/telegram-web-app.js';
    s.onload = () => tryBoot();
    s.onerror = () => tryBoot();
    document.head.appendChild(s);
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: { 'X-Telegram-Init-Data': initData, 'Content-Type': 'application/json', ...(opts.headers || {}) },
    });
    if (!res.ok) { const e = new Error('err'); e.status = res.status; throw e; }
    return res.json();
  }, [initData]);

  useEffect(() => {
    if (!initData) return;
    (async () => {
      try { setMe(await api('/me')); setPhase('ready'); }
      catch (e) { setPhase(e.status === 403 ? 'notbound' : 'notg'); }
    })();
  }, [initData, api]);

  if (phase === 'loading') {
    return <ThemeCtx.Provider value={t}><Center><div className={t.label}>Загрузка…</div></Center></ThemeCtx.Provider>;
  }
  if (phase === 'notg' || phase === 'notbound') {
    const nb = phase === 'notbound';
    return (
      <ThemeCtx.Provider value={t}><Center>
        <div className="text-center max-w-xs">
          <div className="text-5xl mb-4">{nb ? '🔒' : '🏭'}</div>
          <div className={`text-xl font-bold mb-2 ${t.strong}`}>{nb ? 'Нет доступа' : 'Откройте через Telegram'}</div>
          <div className={`text-sm ${t.label}`}>
            {nb ? 'Ваш Telegram не привязан. Запросите персональную ссылку у администратора.'
                : 'Приложение работает внутри бота производства Ainanan. Откройте его кнопкой в чате с ботом.'}
          </div>
        </div>
      </Center></ThemeCtx.Provider>
    );
  }

  return (
    <ThemeCtx.Provider value={t}>
      <div className={`min-h-screen pb-28 ${t.page}`} data-testid="production-app">
        <div className={`sticky top-0 z-20 backdrop-blur border-b px-4 pt-4 pb-2 flex items-center justify-between ${t.header}`}>
          <div>
            <div className="text-lg font-extrabold">🏭 Производство / Склад</div>
            {me?.name && <div className={`text-xs ${t.muted}`}>{me.name}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setHelpOpen(true)} data-testid="help-btn" aria-label="Как пользоваться"
              className={`shrink-0 rounded-full w-10 h-10 flex items-center justify-center text-lg border ${t.surface}`}>❓</button>
            <button onClick={toggleTheme} data-testid="theme-toggle" aria-label="Сменить тему"
              className={`shrink-0 rounded-full w-10 h-10 flex items-center justify-center text-lg border ${t.surface}`}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>
        </div>

        {helpOpen && <HelpPanel onClose={() => setHelpOpen(false)} />}

        <div className="px-4 py-4">
          {tab === 'receipt' && <ReceiptTab api={api} />}
          {tab === 'stock' && <StockTab api={api} />}
          {tab === 'produce' && <ProduceTab api={api} />}
          {tab === 'writeoff' && <WriteOffTab api={api} />}
          {tab === 'ops' && <OpsTab api={api} />}
          {tab === 'req' && <RequirementsTab api={api} />}
        </div>

        <div className={`fixed bottom-0 left-0 right-0 z-30 border-t grid grid-cols-6 ${t.nav} ${t.navShadow}`} style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {TABS.map(([k, icon, lbl]) => {
            const active = tab === k;
            return (
              <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`relative flex flex-col items-center justify-center gap-1 py-3 min-h-[64px] transition-colors duration-150 ${active ? 'text-amber-500' : t.inactiveTab}`}>
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-7 rounded-full bg-amber-500" />}
                <span className="text-xl leading-none">{icon}</span>
                <span className="text-[11px] font-semibold leading-none">{lbl}</span>
              </button>
            );
          })}
        </div>
      </div>
    </ThemeCtx.Provider>
  );
}

function Center({ children }) {
  const t = useT();
  return <div className={`min-h-screen flex items-center justify-center p-6 ${t.center} ${t.page}`}>{children}</div>;
}

function IngredientPicker({ api, value, onChange }) {
  const t = useT();
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  useEffect(() => {
    const tmr = setTimeout(() => { api(`/ingredients?q=${encodeURIComponent(q)}`).then((d) => setList(d.slice(0, 40))).catch(() => {}); }, 250);
    return () => clearTimeout(tmr);
  }, [q, api]);
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск ингредиента…" data-testid="ing-search"
        className={`w-full border rounded-xl px-3 py-2.5 text-sm ${t.surface}`} />
      <div className={`mt-2 max-h-56 overflow-y-auto divide-y rounded-xl border ${t.divide} ${t.border}`}>
        {list.map((i) => (
          <button key={i.id} onClick={() => onChange(i)} data-testid={`ing-${i.id}`}
            className={`w-full text-left px-3 py-2.5 flex items-center justify-between ${value?.id === i.id ? t.selected : ''}`}>
            <span className="text-sm">{i.name} {i.low_stock && <span className="text-red-500">●</span>}</span>
            <span className={`text-xs ${t.muted}`}>{nf.format(i.current_quantity)} {i.unit}</span>
          </button>
        ))}
        {list.length === 0 && <div className={`px-3 py-4 text-center text-xs ${t.muted}`}>—</div>}
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  const t = useT();
  return (
    <div>
      <label className={`text-xs ${t.label}`}>{label}</label>
      <input {...props} className={`w-full border rounded-xl px-3 py-2.5 text-sm mt-1 ${t.surface}`} />
    </div>
  );
}

function Msg({ m }) {
  const t = useT();
  if (!m) return null;
  return <div className={`text-sm rounded-xl px-3 py-2 ${m.ok ? t.okMsg : t.errMsg}`} data-testid="op-result">{m.text}</div>;
}

function ReceiptTab({ api }) {
  const t = useT();
  const [ing, setIng] = useState(null);
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const [supplier, setSupplier] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!ing || !(qty > 0) || !(price >= 0)) { setMsg({ ok: false, text: 'Заполните ингредиент, кол-во и цену' }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api('/receipt', { method: 'POST', body: JSON.stringify({ ingredient_id: ing.id, quantity: Number(qty), unit_price: Number(price), supplier }) });
      setMsg({ ok: true, text: `✅ ${r.name}: остаток ${nf.format(r.current_quantity)} ${ing.unit}, ср. цена ${money(r.new_avg_cost)}` });
      setQty(''); setPrice(''); setSupplier(''); setIng(null);
    } catch (e) { setMsg({ ok: false, text: 'Ошибка сохранения' }); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className={`text-sm font-semibold ${t.heading}`}>📦 Поступление сырья</div>
      <IngredientPicker api={api} value={ing} onChange={setIng} />
      {ing && <div className={`text-xs ${t.label}`}>Выбрано: <b className={t.strong}>{ing.name}</b> ({ing.unit}), ср. цена сейчас {money(ing.cost_per_unit)}</div>}
      <Field label="Количество" type="number" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="receipt-qty" />
      <Field label="Цена за единицу (этой партии), ₸" type="number" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="receipt-price" />
      <Field label="Поставщик (необяз.)" value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="receipt-supplier" />
      <button onClick={submit} disabled={busy} data-testid="receipt-submit" className="w-full bg-amber-600 text-white font-semibold rounded-xl py-3 disabled:opacity-50">{busy ? '…' : 'Принять поступление'}</button>
      <Msg m={msg} />
    </div>
  );
}

function StockTab({ api }) {
  const t = useT();
  const [reorder, setReorder] = useState([]);
  const [all, setAll] = useState([]);
  useEffect(() => {
    api('/reorder').then(setReorder).catch(() => {});
    api('/ingredients').then(setAll).catch(() => {});
  }, [api]);
  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-red-500 mb-2">🛒 К закупке ({reorder.length})</div>
        {reorder.length === 0 ? <div className={`text-xs ${t.muted}`}>Нет позиций ниже минимума</div> : (
          <div className={`divide-y rounded-xl border ${t.divide} ${t.border}`}>
            {reorder.map((i) => (
              <div key={i.id} className="px-3 py-2.5 flex justify-between" data-testid={`reorder-${i.id}`}>
                <span className="text-sm">{i.name}</span>
                <span className={`text-xs ${t.label}`}>{nf.format(i.current_quantity)}/{nf.format(i.min_threshold)} {i.unit}{i.suggested_order ? ` → +${nf.format(i.suggested_order)}` : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className={`text-sm font-semibold mb-2 ${t.heading}`}>📊 Остатки ({all.length})</div>
        <div className={`divide-y rounded-xl border max-h-[55vh] overflow-y-auto ${t.divide} ${t.border}`}>
          {all.map((i) => (
            <div key={i.id} className="px-3 py-2.5 flex justify-between">
              <span className="text-sm">{i.name} {i.low_stock && <span className="text-red-500">●</span>}</span>
              <span className={`text-xs ${t.label}`}>{nf.format(i.current_quantity)} {i.unit}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProduceTab({ api }) {
  const t = useT();
  const [recipes, setRecipes] = useState([]);
  const [rid, setRid] = useState('');
  const [units, setUnits] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api('/recipes').then(setRecipes).catch(() => {}); }, [api]);
  const submit = async () => {
    if (!rid || !(units > 0)) { setMsg({ ok: false, text: 'Выберите изделие и кол-во' }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api('/produce', { method: 'POST', body: JSON.stringify({ recipe_id: rid, units: Number(units) }) });
      setMsg({ ok: true, text: `✅ Выпуск записан: ${r.product_name} — ${nf.format(r.units_produced)} шт (${r.batch_number})` });
      setUnits(''); setRid('');
    } catch (e) { setMsg({ ok: false, text: 'Ошибка' }); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className={`text-sm font-semibold ${t.heading}`}>🏭 Выпуск продукции</div>
      <div className={`text-xs ${t.muted}`}>Фиксирует, сколько произведено (для контроля и перепроизводства). Сырьё не списывает повторно.</div>
      <div>
        <label className={`text-xs ${t.label}`}>Изделие</label>
        <select value={rid} onChange={(e) => setRid(e.target.value)} data-testid="produce-recipe" className={`w-full border rounded-xl px-3 py-2.5 text-sm mt-1 ${t.surface}`}>
          <option value="">— выберите —</option>
          {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>
      <Field label="Количество (шт)" type="number" inputMode="numeric" value={units} onChange={(e) => setUnits(e.target.value)} data-testid="produce-units" />
      <button onClick={submit} disabled={busy} data-testid="produce-submit" className="w-full bg-amber-600 text-white font-semibold rounded-xl py-3 disabled:opacity-50">{busy ? '…' : 'Записать выпуск'}</button>
      <Msg m={msg} />
    </div>
  );
}

function WriteOffTab({ api }) {
  const t = useT();
  const [ing, setIng] = useState(null);
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!ing || !(qty > 0)) { setMsg({ ok: false, text: 'Выберите ингредиент и кол-во' }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api('/write-off', { method: 'POST', body: JSON.stringify({ ingredient_id: ing.id, quantity: Number(qty), reason }) });
      setMsg({ ok: true, text: `✅ Списано: ${r.name}, остаток ${nf.format(r.current_quantity)} ${ing.unit}` });
      setQty(''); setReason(''); setIng(null);
    } catch (e) { setMsg({ ok: false, text: 'Ошибка' }); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className={`text-sm font-semibold ${t.heading}`}>📉 Списание / брак</div>
      <IngredientPicker api={api} value={ing} onChange={setIng} />
      <Field label="Количество к списанию" type="number" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="writeoff-qty" />
      <Field label="Причина (необяз.)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="writeoff-reason" />
      <button onClick={submit} disabled={busy} data-testid="writeoff-submit" className="w-full bg-red-600 text-white font-semibold rounded-xl py-3 disabled:opacity-50">{busy ? '…' : 'Списать'}</button>
      <Msg m={msg} />
    </div>
  );
}

function OpsTab({ api }) {
  const t = useT();
  const [ops, setOps] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => { api('/operations?limit=10').then(setOps).catch(() => {}); }, [api]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (op) => {
    if (!window.confirm(`Отменить операцию?\n${op.title}`)) return;
    setBusyId(op.id); setMsg(null);
    try {
      await api('/operations/cancel', { method: 'POST', body: JSON.stringify({ kind: op.kind, id: op.id }) });
      setMsg({ ok: true, text: '✅ Операция отменена' });
      load();
    } catch (e) { setMsg({ ok: false, text: 'Не удалось отменить' }); }
    finally { setBusyId(null); }
  };

  const tm = (s) => { try { return new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };

  return (
    <div className="space-y-3" data-testid="ops-tab">
      <div className="flex items-center justify-between">
        <div className={`text-sm font-semibold ${t.heading}`}>🕓 Последние 10 операций</div>
        <button onClick={load} className={`text-xs ${t.label}`} data-testid="ops-refresh">обновить</button>
      </div>
      <div className={`text-xs ${t.muted}`}>Ошиблись при вводе? Нажмите «Отменить» — операция будет аннулирована, остатки восстановлены.</div>
      <Msg m={msg} />
      <div className={`divide-y rounded-xl border ${t.divide} ${t.border}`}>
        {ops.map((op) => (
          <div key={`${op.kind}-${op.id}`} className="px-3 py-2.5 flex items-center justify-between gap-2" data-testid={`op-${op.id}`}>
            <div className="min-w-0">
              <div className="text-sm truncate">{op.title}</div>
              <div className={`text-xs ${t.muted}`}>{nf.format(op.qty)} {op.unit} · {tm(op.created_at)}{op.by ? ` · ${op.by}` : ''}</div>
            </div>
            <button onClick={() => cancel(op)} disabled={busyId === op.id} data-testid={`cancel-${op.id}`}
              className={`shrink-0 text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50 ${t.cancelBtn}`}>
              {busyId === op.id ? '…' : 'Отменить'}
            </button>
          </div>
        ))}
        {ops.length === 0 && <div className={`px-3 py-6 text-center text-xs ${t.muted}`}>Операций пока нет</div>}
      </div>
    </div>
  );
}

function RequirementsTab({ api }) {
  const t = useT();
  const isoDay = (off) => { const d = new Date(); d.setDate(d.getDate() + off); return d.toISOString().slice(0, 10); };
  const [from, setFrom] = useState(isoDay(1));
  const [to, setTo] = useState(isoDay(2));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async (f, tt) => {
    setLoading(true); setMsg(null);
    try { setData(await api(`/requirements?date_from=${f}&date_to=${tt}`)); }
    catch (e) { setMsg({ ok: false, text: 'Ошибка расчёта' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(from, to); /* eslint-disable-next-line */ }, []);

  const preset = (a, b) => { const f = isoDay(a), tt = isoDay(b); setFrom(f); setTo(tt); load(f, tt); };
  const notify = async () => {
    setSending(true); setMsg(null);
    try { const r = await api(`/requirements/notify?date_from=${from}&date_to=${to}`, { method: 'POST' }); setMsg({ ok: true, text: `✅ Отправлено в Telegram: ${r.items} позиций` }); }
    catch (e) { setMsg({ ok: false, text: 'Дефицита нет или ошибка отправки' }); }
    finally { setSending(false); }
  };

  const fmtD = (s) => { try { return new Date(s).toLocaleDateString('ru-RU', { weekday: 'short', day: '2-digit', month: 'short' }); } catch (e) { return s; } };

  return (
    <div className="space-y-3" data-testid="req-tab">
      <div className={`text-sm font-semibold ${t.heading}`}>🧮 Потребность по датам</div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => preset(1, 2)} data-testid="req-preset-2d" className={`text-xs font-semibold rounded-lg px-3 py-2 border ${t.surface}`}>Завтра + послезавтра</button>
        <button onClick={() => preset(1, 7)} data-testid="req-preset-7d" className={`text-xs font-semibold rounded-lg px-3 py-2 border ${t.surface}`}>7 дней</button>
      </div>
      <div className="flex items-center gap-2">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="req-from" className={`flex-1 border rounded-xl px-2 py-2 text-sm ${t.surface}`} />
        <span className={t.muted}>—</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="req-to" className={`flex-1 border rounded-xl px-2 py-2 text-sm ${t.surface}`} />
        <button onClick={() => load(from, to)} data-testid="req-load" className="bg-amber-600 text-white text-sm font-semibold rounded-xl px-3 py-2">OK</button>
      </div>

      <Msg m={msg} />
      {loading && <div className={`text-center text-sm py-6 ${t.muted}`}>Загрузка…</div>}

      {data && !loading && (
        <>
          {data.unmapped_items > 0 && (
            <div className={`text-xs rounded-lg px-3 py-2 ${t.errMsg}`}>⚠️ Позиций без рецепта: {data.unmapped_items} — их сырьё не учтено.</div>
          )}

          {data.buy_list.length > 0 ? (
            <div className={`rounded-xl border p-3 ${t.surface}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-red-500">🛒 Купить на период</div>
                <div className="text-xs">Бюджет ≈ <b data-testid="req-budget">{money(data.total_buy_cost)}</b></div>
              </div>
              <div className={`divide-y ${t.divide}`} data-testid="req-buy-list">
                {data.buy_list.map((b) => (
                  <div key={b.ingredient_id} className="py-2 flex items-center justify-between">
                    <span className="text-sm">{b.name}</span>
                    <span className="text-xs text-red-500 font-semibold">+{nf.format(b.buy_qty)} {b.unit}{b.line_cost ? ` ≈ ${money(b.line_cost)}` : ''}</span>
                  </div>
                ))}
              </div>
              <button onClick={notify} disabled={sending} data-testid="req-notify" className="w-full mt-3 bg-amber-600 text-white font-semibold rounded-xl py-2.5 disabled:opacity-50">{sending ? '…' : '📨 Отправить список в Telegram'}</button>
            </div>
          ) : (
            <div className={`text-sm rounded-xl px-3 py-3 ${t.okMsg}`}>✅ Сырья хватает на выбранный период</div>
          )}

          {data.dates.map((d) => (
            <div key={d.date} className={`rounded-xl border overflow-hidden ${t.surface}`}>
              <div className={`px-3 py-2 text-sm font-semibold border-b ${t.border}`}>📅 {fmtD(d.date)} <span className={`text-xs font-normal ${t.muted}`}>· {d.items.length} ингр.</span></div>
              <div className={`divide-y ${t.divide}`}>
                {d.items.map((i) => (
                  <div key={i.ingredient_id} className={`px-3 py-2 flex items-center justify-between ${i.shortage > 0 ? 'text-red-500' : ''}`} data-testid={`req-row-${i.ingredient_id}`}>
                    <span className="text-sm truncate">{i.name}</span>
                    <span className="text-xs whitespace-nowrap">нужно {nf.format(i.need)} · итог <b>{nf.format(i.after)}</b> {i.unit}{i.shortage > 0 ? ` · купить +${nf.format(i.shortage)}` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {data.dates.length === 0 && <div className={`text-center text-xs py-4 ${t.muted}`}>Нет заказов с потребностью на этот период</div>}
        </>
      )}
    </div>
  );
}


function HelpPanel({ onClose }) {
  const t = useT();
  const Item = ({ icon, title, children }) => (
    <div className={`rounded-xl border p-3 ${t.surface}`}>
      <div className="font-semibold text-sm mb-1">{icon} {title}</div>
      <div className={`text-sm ${t.label}`}>{children}</div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex flex-col" data-testid="help-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className={`relative mt-auto rounded-t-2xl max-h-[88vh] overflow-y-auto ${t.page}`} style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className={`sticky top-0 flex items-center justify-between px-4 py-3 border-b ${t.header} backdrop-blur`}>
          <div className="text-base font-bold">❓ Как пользоваться</div>
          <button onClick={onClose} data-testid="help-close" className={`rounded-full w-9 h-9 flex items-center justify-center border ${t.surface}`}>✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className={`text-sm ${t.muted}`}>Коротко: продажи списывают сырьё сами. Вам нужно вносить только <b>приход</b> (что закупили).</div>

          <div className={`text-xs font-semibold uppercase ${t.muted} pt-1`}>Что делать вручную</div>
          <Item icon="📦" title="Приход — главное">Закупили сырьё/упаковку? Откройте «Приход», выберите ингредиент, укажите количество и цену партии. Остаток вырастет, средняя цена пересчитается сама.</Item>
          <Item icon="📉" title="Списание / брак">Только если что-то испортилось или выбросили сверх нормы. По надобности.</Item>
          <Item icon="🏭" title="Выпуск (по желанию)">Счётчик «сколько испекли». На остаток сырья НЕ влияет — нужен для контроля производства.</Item>
          <Item icon="🕓" title="Операции — отмена">Ошиблись при вводе? Откройте «Операции» и нажмите «Отменить» — остаток восстановится.</Item>

          <div className={`text-xs font-semibold uppercase ${t.muted} pt-1`}>Что считается автоматически</div>
          <Item icon="🛒" title="Продажи и заказы">Когда товар продан — сырьё списывается по рецепту само. Отмена заказа возвращает сырьё.</Item>
          <Item icon="📊" title="Остатки и закупка">Вкладки «Остатки» и «Закупка» показывают, что есть и что докупить под заказы по датам — считается само.</Item>

          <div className={`text-xs ${t.muted} pt-1`}>⚙️ Цены ингредиентов, привязку рецептов и нормы Min/Max настраивает администратор в веб-панели.</div>
          <button onClick={onClose} className="w-full bg-amber-600 text-white font-semibold rounded-xl py-3 mt-2" data-testid="help-ok">Понятно</button>
        </div>
      </div>
    </div>
  );
}
